import ContentPasteSearchIcon from '@mui/icons-material/ContentPasteSearch';
import SendIcon from '@mui/icons-material/Send';
import { Box, Tooltip, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { CoachingOverviewSafetyScoreSet, ShortCoachSession } from '../../../../backendsdk';
import { ScoreAvatar } from '../../../../components/ScoreAvatar';
import { TrackedIconButton as IconButton } from '../../../../components/TrackedComponents';
import palette from '../../../ColorPalette';
import { StyledTableCell, StyledTableRow } from './common';

interface CoachingEffectivenessRowProps {
    row: CoachingOverviewSafetyScoreSet;
    autoSessions: ShortCoachSession[];
    tabsMap: Record<string, number>;
    setTab: (tabIndex: number) => void;
    onClose: () => void;
    idx: number;
}

const CoachingEffectivenessRow: React.FC<CoachingEffectivenessRowProps> = (props: CoachingEffectivenessRowProps) => {
    const { t } = useTranslation();
    const history = useHistory();
    const { row, autoSessions, tabsMap, setTab, onClose, idx } = props;

    const driverName = row.driver_name ? row.driver_name : t('content.coach.overview.effectiveness.unknown_driver');
    const scores = [row.on_completion, row.two_weeks, row.one_month, row.three_months];
    const autoSession = autoSessions.find((session) => session.driver_name === row.driver_name);
    const handleSendSession = () => {
        setTab(tabsMap['suggested']);
        history.push(`/coaching/${autoSession?.session_id}/send`);
        onClose();
    };

    const handleViewSession = () => {
        setTab(tabsMap['completed']);
        history.push(`/coaching/${row.session_id}`);
        onClose();
    };

    const newSessionButton = (
        <IconButton id={`send-session-btn-${idx}`} disabled={autoSession === undefined} onClick={handleSendSession}>
            <SendIcon />
        </IconButton>
    );

    return (
        <StyledTableRow>
            <StyledTableCell component="th" scope="row">
                <Typography component="span" sx={{ color: !row.driver_name ? palette.neutral[700] : undefined }}>
                    {driverName}
                </Typography>
            </StyledTableCell>
            {scores.map((score, idx) => (
                <StyledTableCell key={`score-${idx}`}>
                    {score !== undefined ? (
                        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                            <ScoreAvatar
                                score={score.score}
                                inactive={score.reason === 'inactive'}
                                unknown={score.reason === 'unknown'}
                            />
                        </Box>
                    ) : null}
                </StyledTableCell>
            ))}
            <StyledTableCell>
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                    <Tooltip title={t('content.coach.overview.effectiveness.view_session')}>
                        <Box>
                            <IconButton id={`view-session-btn-${idx}`} onClick={handleViewSession}>
                                <ContentPasteSearchIcon />
                            </IconButton>
                        </Box>
                    </Tooltip>
                </Box>
            </StyledTableCell>
            <StyledTableCell>
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                    {autoSession ? (
                        <Tooltip title={t('content.coach.overview.effectiveness.send_new_session')}>
                            <Box>{newSessionButton}</Box>
                        </Tooltip>
                    ) : (
                        newSessionButton
                    )}
                </Box>
            </StyledTableCell>
        </StyledTableRow>
    );
};

export default CoachingEffectivenessRow;
